import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useStorageState } from '@ssg/common/Hooks/useLocalStorage';
import { removeTypenameGeneric } from '@ssg/common/Helpers/typenameHelper';
import { faCheck, faFilePdf, faSync, faTimes, faTrash, faEdit } from '@fortawesome/pro-regular-svg-icons';
import {
	CaseOrganizationWorkerInput,
	EsdhFileFragmentFragment,
	useCreateCaseOrganizationReportMutation,
	useGetCaseAsbestosReportsAndDocumentsQuery,
	useGetWebCaseQuery,
} from '@ssg/common/GraphQL/indexV2';
import { useFlag } from '@unleash/proxy-client-react';
import { FeatureFlagEnums } from '@ssg/common/FeatureFlagEnums';
import { checkFilesExist, tryGetFile } from './checkFilesExist';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormFieldHeader from '../../../Components/Forms/FormFieldHeader';
import Input from '@ssg/common/Components/Input';
import ReportStaticInfo from './ReportStaticInfo';
import Button from '@ssg/common/Components/Button';
import Header from '@ssg/common/Components/Header';
import Box from '../../../Components/Layout/Box';
import Loading from '@ssg/common/Components/Loading';
import Datepicker from '@ssg/common/Components/Datepicker';
import Modal from '@ssg/common/Components/Modal';
import SignatureInput from './Inputs/SignatureInput';
import EnvironmentVariableContext from '@ssg/common/EnvironmentVariableContext';
import { formatDateForInput } from '@ssg/common/Helpers/dateToDateOnlyString';
import { useUploadSignature } from './hooks/useUploadSignature';

interface Params {
	id: string;
}

interface ReportError {
	key: keyof CaseOrganizationWorkerInput;
	index: number;
}

const TABLE_COLUMNS: Array<keyof CaseOrganizationWorkerInput> = ['title', 'name', 'phonenumber', 'date', 'instructor', 'signature'];
interface SigningWorker {
	workerIndex: number;
}
const EMPTY_ROW = (index: number): CaseOrganizationWorkerInput => ({
	index,
	title: '',
	name: '',
	phonenumber: '',
	instructor: '',
	date: '',
	signature: null,
});
const CaseOrganizationReport: React.FC = () => {
	const { id: caseId } = useParams<Params>();
	const { t } = useTranslation();
	const history = useHistory();

	const envVar = React.useContext(EnvironmentVariableContext);
	const saveButtonReportsFlag = useFlag(FeatureFlagEnums.SAVE_BUTTON_REPORTS);

	const [reportErrors, setReportErrors] = React.useState<ReportError[]>([]);
	const [fileError, setFileError] = React.useState<boolean[]>([]);
	const [loadingType, setLoadingType] = React.useState<'CREATE' | 'SAVE'>('CREATE');
	const [saved, setSaved] = React.useState(false);
	const { data: caseData } = useGetWebCaseQuery({ variables: { id: caseId } });
	const erpNumber = caseData?.case.erpNo || '';
	const [signingWorker, setSigningWorker] = React.useState<SigningWorker>();
	const { loading: caseReportsAndDocumentsLoading } = useGetCaseAsbestosReportsAndDocumentsQuery({
		variables: {
			id: caseId,
		},
		skip: !caseId || caseId === '',
		onCompleted(data) {
			const formData = removeTypenameGeneric(data.case.caseAsbestosReports?.caseOrganizationFormData?.caseOrganizationWorkers);
			if (formData !== null && formData !== undefined && formData.length !== 0) {
				setCaseOrganizationWorkers(formData as CaseOrganizationWorkerInput[]);

				const fileNames = formData
					.filter(
						worker =>
							worker !== undefined &&
							worker !== null &&
							worker.signature !== undefined &&
							worker.signature !== null &&
							worker.signature.signatureDetails !== null &&
							worker.signature.signatureDetails.fileName,
					)
					.map(f => {
						return { fileName: f!.signature!.signatureDetails!.fileName, folder: undefined };
					});

				const existingFiles = checkFilesExist(fileNames, data.case.documents);

				caseOrganizationWorkers.forEach((worker, index) => {
					if (existingFiles.some(ef => ef.fileName === worker.signature?.signatureDetails?.fileName)) {
						const file = tryGetFile(worker.signature!.signatureDetails!.fileName, data.case.documents);

						setSignatureFiles(prevFiles => {
							const updatedFiles = [...(prevFiles || [])];
							updatedFiles[index] = file;
							return updatedFiles;
						});
					} else {
						setSignatureFiles(prevFiles => {
							const updatedFiles = [...(prevFiles || [])];
							updatedFiles[index] = null;
							return updatedFiles;
						});
					}
				});
			}
		},
		fetchPolicy: 'cache-and-network',
	});

	const useLocalStorage = <T,>(name: string, initialValue: T) => useStorageState<T>(window.localStorage, `${caseId}-case-organization-${name}`, initialValue);
	const [caseOrganizationWorkers, setCaseOrganizationWorkers] = useLocalStorage<CaseOrganizationWorkerInput[]>('caseOrganizationWorker-caseOrganization', []);
	const [createCaseOrganizationReport, { loading: submitting }] = useCreateCaseOrganizationReportMutation();
	const [signatureFiles, setSignatureFiles] = React.useState<(EsdhFileFragmentFragment | null)[]>();
	React.useEffect(() => {
		setSaved(false);
	}, [caseOrganizationWorkers]);

	const generateBaseOrganization = () => {
		if (caseOrganizationWorkers.length > 0) {
			return;
		}
		const newWorkers: CaseOrganizationWorkerInput[] = [];
		if (caseData?.case.areaResponsible) {
			newWorkers.push({
				index: 0,
				title: t('reports.asbestos.areaResponsible'),
				name: caseData?.case.areaResponsible.name ?? '',
				instructor: '',
				phonenumber: caseData?.case.areaResponsible.phone ?? '',
			});
		}

		if (caseData?.case.projectManager) {
			newWorkers.push({
				index: caseOrganizationWorkers.some(worker => worker.index === 0) ? 1 : 0,
				title: t('reports.asbestos.projectManager'),
				name: caseData?.case.projectManager.name ?? '',
				instructor: '',
				phonenumber: caseData?.case.projectManager.phone ?? '',
			});
		}
		if (newWorkers.length > 0) {
			setCaseOrganizationWorkers(newWorkers);
		}
	};
	React.useEffect(() => {
		generateBaseOrganization();
	}, [caseData]);

	async function submitReport() {
		const errors = errorCheck(caseOrganizationWorkers);
		setReportErrors(errors);

		try {
			setLoadingType('CREATE');
			await createCaseOrganizationReport({
				variables: {
					caseId,
					formData: {
						caseOrganizationWorkers: formatWorkers(caseOrganizationWorkers) ?? [],
					},
					onlySave: false,
				},
			});

			history.goBack();
		} catch (e) {
			console.error(e);
		}
	}
	async function saveReport() {
		try {
			setLoadingType('SAVE');
			await createCaseOrganizationReport({
				variables: {
					caseId,
					formData: {
						caseOrganizationWorkers: formatWorkers(caseOrganizationWorkers) ?? [],
					},
					onlySave: true,
				},
			});
			setSaved(true);
		} catch (e) {
			console.error(e);
		}
	}

	const handleChange = (index: number, column: keyof CaseOrganizationWorkerInput, value: any) => {
		setCaseOrganizationWorkers(current =>
			current.map((worker, i) =>
				i === index
					? {
							...worker,
							[column]: value,
					  }
					: worker,
			),
		);
	};
	function addRow() {
		const lineNumber = caseOrganizationWorkers.length === 0 ? 0 : caseOrganizationWorkers[caseOrganizationWorkers.length - 1].index + 1;
		setCaseOrganizationWorkers(current => [...current, EMPTY_ROW(lineNumber)]);
	}

	function removeRow(idx: number) {
		setCaseOrganizationWorkers(caseOrganizationWorkers.filter((_, i) => i !== idx));
	}

	const errorCheck = (workers: CaseOrganizationWorkerInput[]) => {
		const errors: ReportError[] = [];
		workers.forEach((worker, i) => {
			//
		});

		return errors;
	};

	const resetForm = () => {
		setCaseOrganizationWorkers([]);
	};
	const { isUploading, uploadSignature } = useUploadSignature();

	if (caseReportsAndDocumentsLoading) return <Loading />;

	return (
		<>
			<div>
				<Header title="reports.reportTypes.caseOrganization" backButton />

				<div className="space-y-8 px-10 pb-10 text-sm">
					<ReportStaticInfo caseId={caseId} />

					<Box full icon={faSync} iconText="reports.reset" title="" onClick={() => resetForm()}>
						<div className="w-full">
							<div className="flex gap-2">
								{TABLE_COLUMNS.map(column => (
									<div key={column} className="flex-1">
										<FormFieldHeader title={t(`reports.asbestos.${column}`)} />
									</div>
								))}
							</div>

							{caseOrganizationWorkers.map((worker, idx) => {
								return (
									<div key={idx} className="flex gap-2 pb-2">
										{TABLE_COLUMNS.map((column, i) => {
											return (
												<div key={column} className="flex flex-1">
													{column === 'date' && (
														<Datepicker title={''} name={column} value={worker[column] ?? ''} onChange={e => handleChange(idx, column, e.target.value)} />
													)}
													{column === 'signature' && (
														<div className="w-full flex items-center gap-2">
															{signatureFiles?.some(file => file?.name && file?.name === worker.signature?.signatureDetails?.fileName) ? (
																<>
																	<p className="w-32">{t('reports.asbestos.signed')} </p>
																	<FontAwesomeIcon icon={faCheck} className="text-green" size="lg" />
																</>
															) : (
																<>
																	<p className="w-32">{t('reports.asbestos.notSigned')} </p>
																	<FontAwesomeIcon icon={faTimes} className="text-red" size="lg" />
																</>
															)}

															<FontAwesomeIcon
																icon={faEdit}
																size="lg"
																className="cursor-pointer"
																onClick={() => {
																	setSigningWorker({ workerIndex: idx });
																}}
															/>
														</div>
													)}
													{column !== 'date' && column !== 'signature' && (
														<Input name={column} value={worker[column] ?? ''} onChange={e => handleChange(idx, column, e.target.value)} />
													)}
													{i === TABLE_COLUMNS.length - 1 && <FontAwesomeIcon icon={faTrash} className="text-red cursor-pointer " size="lg" onClick={() => removeRow(idx)} />}
												</div>
											);
										})}
									</div>
								);
							})}

							<Button onClick={addRow} primary>
								{t('reports.addIndoorClimateMeassurement')}
							</Button>
						</div>
					</Box>

					<div className="ml-4 my-2 mr-6 flex flex-row space-x-2">
						<div className="my-2 flex flex-row space-x-2">
							<Button icon={faFilePdf} onClick={() => submitReport()} disabled={submitting} loading={submitting && loadingType === 'CREATE'} success text="reports.createPdfReport" />

							{saveButtonReportsFlag && (
								<Button
									disabled={submitting}
									loading={submitting && loadingType === 'SAVE'}
									primary
									onClick={() => saveReport()}
									text={saved ? '' : 'reports.saveReport'}
									icon={saved ? faCheck : undefined}
								/>
							)}
						</div>
					</div>
				</div>
			</div>
			{signingWorker && signingWorker.workerIndex !== undefined && (
				<Modal
					visible={signingWorker.workerIndex !== undefined}
					close={() => {
						setSigningWorker(undefined);
					}}
					title={''}
					body={
						<div className="p-1">
							<SignatureInput
								required={false}
								label={t('reports.asbestos.signature')}
								isUploading={isUploading}
								signatureFile={signatureFiles?.[signingWorker.workerIndex] ?? undefined}
								onClear={() => {
									handleChange(signingWorker.workerIndex, 'signature', undefined);
									setSignatureFiles(prevFiles => {
										const updatedFiles = [...(prevFiles || [])];
										updatedFiles[signingWorker.workerIndex] = null;
										return updatedFiles;
									});
								}}
								onSubmit={async signature => {
									try {
										const fileName = `signature-${Date.now()}.${signature.extension}`;
										const uploadResponse = await uploadSignature(signature.base64, fileName, erpNumber);

										if (uploadResponse?.ok) {
											setSignatureFiles(prevFiles => {
												const updatedFiles = [...(prevFiles || [])];
												updatedFiles[signingWorker.workerIndex] = {
													thumbnail: signature.base64,
													extension: signature.extension,
													url: '',
													created: '',
													name: fileName,
													fileId: '',
													groupingKey: '',
													metadata: [],
													comments: '',
												};
												return updatedFiles;
											});
											handleChange(signingWorker.workerIndex, 'signature', { signatureDate: formatDateForInput(new Date()), signatureDetails: { fileName: fileName } });
											setSigningWorker(undefined);
										} else {
											setSignatureFiles(prevFiles => {
												const updatedFiles = [...(prevFiles || [])];
												updatedFiles[signingWorker.workerIndex] = null;
												return updatedFiles;
											});
										}
									} catch (error) {
										console.error(error);
										setFileError(prevErrors => {
											const updatedErrors = [...(prevErrors || [])];
											updatedErrors[signingWorker.workerIndex] = true;
											return updatedErrors;
										});
									}
								}}
								errorMessage={fileError[signingWorker.workerIndex] ? t('common.couldNotUpload') : undefined}
							/>
						</div>
					}
				/>
			)}
		</>
	);
};

export default CaseOrganizationReport;
function formatWorkers(workers: CaseOrganizationWorkerInput[]) {
	return workers.map(worker => ({
		...worker,
		date: worker.date === '' ? null : worker.date, // Set empty dates to null
	}));
}
